import React, { useState } from 'react';
import { Package, CheckCircle2, Truck, MapPin } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { trackingHistory } from '../mockData';
import { useToast } from '../hooks/use-toast';

const TrackingSection = () => {
  const [trackingNumber, setTrackingNumber] = useState('');
  const [showTracking, setShowTracking] = useState(false);
  const { toast } = useToast();

  const handleTrack = () => {
    // Redirect to tracking page
    window.location.href = '/track-shipment';
  };

  return (
    <section id="track" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16" data-aos="fade-up">
          <h2 className="text-4xl font-bold text-gray-900 mb-4 relative">
            Track Your Shipment
            {/* Subtle animated line */}
            <span className="tracking-progress-line absolute bottom-0 left-1/2 transform -translate-x-1/2 w-32 h-0.5 bg-red-600"></span>
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Real-time tracking for complete transparency and peace of mind
          </p>
        </div>

        {/* Tracking Input */}
        <div className="max-w-2xl mx-auto mb-12" data-aos="fade-up" data-aos-delay="200">
          <Card className="shadow-lg hover:shadow-2xl transition-all duration-300">
            <CardHeader>
              <CardTitle>Enter Tracking Number</CardTitle>
              <CardDescription>Track your package in real-time</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <Input
                  placeholder="Enter tracking number"
                  value={trackingNumber}
                  onChange={(e) => setTrackingNumber(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleTrack()}
                  className="h-12"
                />
                <Button 
                  onClick={handleTrack}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-8 h-12"
                >
                  Track
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tracking Results */}
        {showTracking && (
          <div className="max-w-4xl mx-auto">
            <Card className="shadow-lg">
              <CardHeader className="bg-red-50">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-2xl">Tracking: {trackingNumber}</CardTitle>
                    <CardDescription className="text-lg mt-1">Current Status: Delivered</CardDescription>
                  </div>
                  <div className="bg-green-500 text-white px-4 py-2 rounded-full flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5" />
                    <span className="font-semibold">Delivered</span>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="space-y-6">
                  {trackingHistory.map((item, index) => (
                    <div key={index} className="flex gap-4 relative">
                      {/* Timeline line */}
                      {index !== trackingHistory.length - 1 && (
                        <div className="absolute left-6 top-12 w-0.5 h-full bg-red-200"></div>
                      )}
                      
                      {/* Icon */}
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 z-10 ${
                        index === 0 ? 'bg-green-500' : 'bg-red-600'
                      }`}>
                        {index === 0 ? (
                          <CheckCircle2 className="w-6 h-6 text-white" />
                        ) : index === 1 ? (
                          <Truck className="w-6 h-6 text-white" />
                        ) : (
                          <Package className="w-6 h-6 text-white" />
                        )}
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1 pb-8">
                        <div className="bg-gray-50 p-4 rounded-lg hover:bg-gray-100 transition-colors">
                          <div className="flex items-start justify-between mb-2">
                            <h4 className="font-semibold text-lg text-gray-900">{item.status}</h4>
                            <span className="text-sm text-gray-500">{item.time}</span>
                          </div>
                          <div className="flex items-center text-gray-600 mb-1">
                            <MapPin className="w-4 h-4 mr-1 text-red-600" />
                            <span className="text-sm">{item.location}</span>
                          </div>
                          <p className="text-sm text-gray-600">{item.description}</p>
                          <p className="text-xs text-gray-500 mt-1">{item.date}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </section>
  );
};

export default TrackingSection;